'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import MaterialIcons from '../MaterialIcons/materialIcons';

interface ProductData {
  icon: Array<{
    url: string;
    title?: string;
  }> | null;
  product_name: string;
  description?: string;
  url?: string;
}

interface ProductSlideMenuProps {
  isOpen: boolean;
  onClose: () => void;
  products: ProductData[];
  headerHeight?: number;
  isLoading?: boolean;
  dashboardSection?: React.ReactNode;
  onProductClick?: () => void;
}

export function ProductSlideMenu({
  isOpen,
  onClose,
  products,
  headerHeight = 0,
  isLoading = false,
  dashboardSection,
  onProductClick,
}: ProductSlideMenuProps) {
  return (
    <div
      className={`fixed z-[10000] transition-opacity duration-300 ease-in-out ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
      style={{
        top: `${headerHeight}px`,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      <div className="flex flex-col h-full w-full">
        {/* Dashboard Section - if provided, stays fixed */}
        {dashboardSection && (
          <div className="flex-shrink-0 bg-surface-3">
            {dashboardSection}
          </div>
        )}
        
        {/* Slide Menu - only the products content slides in from right */}
        <div
          className={`pt-2 flex-1 bg-surface-2 shadow-xl transition-transform duration-300 ease-in-out overflow-y-auto ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Products List */}
            <div className="flex flex-col flex-1">
              {/* Back button - styled like other menu items */}
              <div >
                <button
                  onClick={onClose}
                  className="flex items-center gap-3 px-4 py-2 transition-colors whitespace-nowrap text-left hover:bg-surface-3 text-text-default-medium touch-manipulation"
                  aria-label="Back"
                >
                  <MaterialIcons iconName="chevron_left" className="!text-[20px] text-icon-soft" />
                  <span className="cs-s-body">Back</span>
                </button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center h-full p-8">
                  <p className="cs-s-body text-text-default-medium text-center">
                    Loading products...
                  </p>
                </div>
              ) : products.length === 0 ? (
                <div className="flex items-center justify-center h-full p-8">
                  <p className="cs-s-body text-text-default-medium text-center">
                    No products available
                  </p>
                </div>
              ) : (
                <>
                  {/* Group products by icon presence */}
                  {(() => {
                    // Helper function to get icon
                    const getIcon = (icons: Array<{ url: string; title?: string }> | null) => {
                      if (!icons || icons.length === 0) return null;
                      const darkIcon = icons.find(icon => 
                        icon.url && icon.url.match(/-[Dd]ark/i)
                      );
                      return darkIcon || icons[0];
                    };

                    // Separate products into those with icons and those without
                    const productsWithIcons = products.filter(product => {
                      const selectedIcon = getIcon(product.icon);
                      return selectedIcon?.url;
                    });

                    const productsWithoutIcons = products.filter(product => {
                      const selectedIcon = getIcon(product.icon);
                      return !selectedIcon?.url;
                    });

                    return (
                      <>
                        {/* Group 1: Products with icons */}
                        {productsWithIcons.length > 0 && (
                          <div className="flex flex-col">
                            {productsWithIcons.map((product, index) => {
                              const productSlug = product.product_name.toLowerCase().replace(/\s+/g, '-');
                              const fallbackUrl = `/product/${productSlug}`;
                              const rawUrl = (product.url || '').trim();
                              const isAbsolute = rawUrl.startsWith('http://') || rawUrl.startsWith('https://');
                              const productUrl = rawUrl
                                ? isAbsolute
                                  ? rawUrl
                                  : (() => {
                                      const path = rawUrl.replace(/^\//, '');
                                      return path.startsWith('product/') ? `/${path}` : `/product/${path}`;
                                    })()
                                : fallbackUrl;
                              const selectedIcon = getIcon(product.icon);
                              const iconUrl = selectedIcon?.url || null;
                              const iconTitle = selectedIcon?.title || product.product_name;

                              return (
                                <Link
                                  key={`with-icon-${index}`}
                                  href={productUrl}
                                  target={isAbsolute ? '_blank' : undefined}
                                  rel={isAbsolute ? 'noopener noreferrer' : undefined}
                                  onClick={() => {
                                    onClose();
                                    onProductClick?.();
                                  }}
                                  className="flex items-center gap-4 px-4 py-2 transition-colors whitespace-nowrap text-left hover:bg-surface-3 text-text-default-medium touch-manipulation"
                                >
                                  {/* Product Icon */}
                                  {iconUrl && (
                                    <div className="relative w-5 h-5 shrink-0 flex items-center justify-center">
                                      <Image
                                        src={iconUrl}
                                        alt={iconTitle}
                                        width={20}
                                        height={20}
                                        className="object-contain"
                                      />
                                    </div>
                                  )}
                                  
                                  {/* Product Name */}
                                  <span className="flex-1 cs-s-body-light">{product.product_name}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}

                        {/* Border with 8px gap top and bottom - full width including outer padding */}
                        {productsWithIcons.length > 0 && productsWithoutIcons.length > 0 && (
                          <div className="border-t border-border-medium my-2 w-full" />
                        )}

                        {/* Group 2: Products without icons */}
                        {productsWithoutIcons.length > 0 && (
                          <div className="flex flex-col">
                            {productsWithoutIcons.map((product, index) => {
                              const productSlug = product.product_name.toLowerCase().replace(/\s+/g, '-');
                              const fallbackUrl = `/product/${productSlug}`;
                              const rawUrl = (product.url || '').trim();
                              const isAbsolute = rawUrl.startsWith('http://') || rawUrl.startsWith('https://');
                              const productUrl = rawUrl
                                ? isAbsolute
                                  ? rawUrl
                                  : (() => {
                                      const path = rawUrl.replace(/^\//, '');
                                      return path.startsWith('product/') ? `/${path}` : `/product/${path}`;
                                    })()
                                : fallbackUrl;

                              return (
                                <Link
                                  key={`no-icon-${index}`}
                                  href={productUrl}
                                  target={isAbsolute ? '_blank' : undefined}
                                  rel={isAbsolute ? 'noopener noreferrer' : undefined}
                                  onClick={() => {
                                    onClose();
                                    onProductClick?.();
                                  }}
                                  className="flex items-center gap-4 px-4 py-2 transition-colors whitespace-nowrap text-left hover:bg-surface-3 text-text-default-medium touch-manipulation"
                                >
                                  <span className="flex-1 cs-s-body-light">{product.product_name}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

